import {
  Box,
  Button,
  ButtonVariants,
  FormControlSizes,
  FormGroup,
  Grid,
  HelpIcon,
  HStack,
  Input,
  Stack,
  Text,
  ToggleGroup,
} from '@talos/kyoko';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { AggregatedAssetSelect } from './AggregatedAssetSelect';
import { AGGREGATION_METHOD_OPTIONS, type AggregationMethod } from './AggregatedMetric.types';
import { AggregatedMetricSelect } from './AggregatedMetricSelect';

interface AggregatedMetricConfigViewProps {
  /** Current user-edited tab title; empty string when the user hasn't customized it. */
  title: string;
  /** Shown as the input placeholder when `title` is empty — the panel's fallback label. */
  titlePlaceholder: string;
  /** Fires on blur or Enter — the parent persists then and mirrors to the dockview tab. */
  onTitleChange: (next: string) => void;
  /** Currently active CoinMetrics metric ID. Persisted by the parent. */
  metric: string;
  /**
   * Selectable metrics — the intersection of metrics supported by every selected
   * asset. Empty when no assets are selected; the dropdown disables in that case.
   */
  availableMetrics: string[];
  /** Catalog query loading state (drives the metric picker's disabled/loading copy). */
  isMetricsLoading: boolean;
  /** Catalog query error state (drives the metric picker's disabled/error copy). */
  isMetricsError: boolean;
  onMetricChange: (next: string) => void;
  /** Maps a metric ID to its display label. */
  getMetricLabel: (metricId: string) => string;
  /** Currently selected assets as Talos symbols (e.g. `['BTC','ETH']`). */
  selectedTalosSymbols: string[];
  /** All Talos symbols available for the active metric (the asset checklist options). */
  availableTalosSymbols: string[];
  /** Fires live on every asset check/uncheck — the parent persists immediately. */
  onSelectedTalosSymbolsChange: (talosSymbols: string[]) => void;
  aggregationMethod: AggregationMethod;
  onAggregationMethodChange: (method: AggregationMethod) => void;
  onClose: () => void;
}

/**
 * In-panel config view: an editable tab title, an asset multi-select, a metric picker
 * constrained to the intersection of metrics supported by every selected asset, and a
 * SUM/MEAN aggregation toggle. A full-width Done button sits pinned to the bottom and
 * returns the panel to its chart view.
 *
 * The asset control is the same `AggregatedAssetSelect` the chart toolbar uses, so
 * the two surfaces stay consistent. Layout mirrors `SingleSymbolPanelSettings`. Field
 * changes other than the title are live; the title commits on blur or Enter to avoid
 * thrashing the dockview tab on every keystroke.
 */
export function AggregatedMetricConfigView({
  title,
  titlePlaceholder,
  onTitleChange,
  metric,
  availableMetrics,
  isMetricsLoading,
  isMetricsError,
  onMetricChange,
  getMetricLabel,
  selectedTalosSymbols,
  availableTalosSymbols,
  onSelectedTalosSymbolsChange,
  aggregationMethod,
  onAggregationMethodChange,
  onClose,
}: AggregatedMetricConfigViewProps) {
  // Local input state so the tab title only updates when the user is done typing
  // (blur or Enter). Keystroke-by-keystroke updates would thrash the dockview tab
  // and persist every intermediate value to the layout config.
  const [titleDraft, setTitleDraft] = useState(title);
  // Pull in updates that originate outside this view — e.g. another panel renames us,
  // or the parent clears the field. Skips when the draft already matches to preserve
  // any in-flight edits the user has typed but not yet committed.
  useEffect(() => {
    setTitleDraft(prev => (prev === title ? prev : title));
  }, [title]);

  // Latest values captured in refs so `commitTitle` can be a stable callback —
  // otherwise it rebuilds on every keystroke (since `titleDraft` is in its deps)
  // and the Input churns its `onBlur` listener every render.
  const titleDraftRef = useRef(titleDraft);
  titleDraftRef.current = titleDraft;
  const committedTitleRef = useRef(title);
  committedTitleRef.current = title;
  const onTitleChangeRef = useRef(onTitleChange);
  onTitleChangeRef.current = onTitleChange;

  const commitTitle = useCallback(() => {
    const next = titleDraftRef.current;
    if (next !== committedTitleRef.current) {
      onTitleChangeRef.current(next);
    }
  }, []);

  const handleTitleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  }, []);

  return (
    <>
      <Box
        px="spacing12"
        py="spacing8"
        borderColor="backgroundDivider"
        borderBottom="solid 1px"
        data-testid="aggregated-metric-config-header"
      >
        <Text fontSize="fontSizeMd">Aggregated Metric Settings</Text>
      </Box>
      <Stack p="spacing12" overflow="auto" scrollbarGutter="stable" pr="spacing4">
        <FormGroup label="Title">
          <Input
            value={titleDraft}
            placeholder={titlePlaceholder}
            onChange={e => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={handleTitleKeyDown}
            size={FormControlSizes.Small}
            data-testid="aggregated-metric-config-title"
          />
        </FormGroup>
        <FormGroup label="Assets">
          <AggregatedAssetSelect
            availableTalosSymbols={availableTalosSymbols}
            selectedTalosSymbols={selectedTalosSymbols}
            onSelectedTalosSymbolsChange={onSelectedTalosSymbolsChange}
            buttonTestID="aggregated-metric-config-assets"
            dropdownTestID="aggregated-metric-config-assets-dropdown"
            // The config panel clips overflow; portal the menu so it isn't cut off by the panel bounds.
            portalize
          />
        </FormGroup>
        <FormGroup
          label={
            <HStack gap="spacing4" alignItems="center">
              Metric
              <HelpIcon
                usePortal
                tooltip="Only metrics supported by every selected asset are listed. Add or remove assets to widen or narrow the choices."
              />
            </HStack>
          }
        >
          <AggregatedMetricSelect
            metric={metric}
            availableMetrics={availableMetrics}
            isLoading={isMetricsLoading}
            isError={isMetricsError}
            hasSelectedAssets={selectedTalosSymbols.length > 0}
            onMetricChange={onMetricChange}
            getLabel={getMetricLabel}
            testID="aggregated-metric-config-metric"
          />
        </FormGroup>
        <FormGroup label="Aggregation">
          <ToggleGroup
            value={aggregationMethod}
            defaultSelectedVariant={ButtonVariants.Priority}
            options={AGGREGATION_METHOD_OPTIONS}
            onChange={onAggregationMethodChange}
            size={FormControlSizes.Small}
            data-testid="aggregated-metric-config-aggregation"
          />
        </FormGroup>
      </Stack>
      <Grid
        mt="auto"
        gap="spacing8"
        p="spacing12"
        gridTemplateColumns="1fr"
        borderColor="backgroundDivider"
        borderTop="solid 1px"
      >
        <Button onClick={onClose} size={FormControlSizes.Small} data-testid="aggregated-metric-config-done">
          Done
        </Button>
      </Grid>
    </>
  );
}
